import React, { useState, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import TextArea from '../../components/Form/TextArea';
import Button from '../../components/Form/Button';
import { useCreateTransaction, useUpdateTransaction } from '../../hooks/useTransactions';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import {
  OutBasicInfoSection,
  OutPaymentMethodSection,
  OutBankDetailsSection,
  OutSafeDetailsSection,
  OutRecipientSection,
  useOutTransactionOptions,
} from './OutTransactionSections';
import type { OutTransactionFormData } from './OutTransactionSections';
import { transactionToOutFormValues } from './transactionEditHelpers';

interface OutTransactionFormProps {
  /** When provided, the form edits this transaction instead of creating one */
  transaction?: any;
  onSaved?: () => void;
  onCancel?: () => void;
}

const OutTransactionForm: React.FC<OutTransactionFormProps> = ({ transaction, onSaved, onCancel }) => {
  const isEdit = !!transaction;
  const [recipientType, setRecipientType] = useState<'employee' | 'supplier'>(
    transaction?.recipientType === 'supplier' ? 'supplier' : 'employee'
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    banks, safes, currencies, branches, employees, providers, bills,
    bankOptions, safeOptions, branchOptions,
    employeeOptions, providerOptions, billOptions, currencyOptions,
    isLoading, banksLoading, safesLoading, branchesLoading,
    employeesLoading, providersLoading, billsLoading,
  } = useOutTransactionOptions(transaction?.bill);

  const { execute: createTransaction, isLoading: createLoading } = useCreateTransaction(
    () => {
      setSubmitSuccess(true);
      setSubmitError(null);
      reset();
      setTimeout(() => setSubmitSuccess(false), 3000);
    }
  );

  const { execute: updateTransaction, isLoading: updateLoading } = useUpdateTransaction(
    () => {
      setSubmitError(null);
      onSaved?.();
    }
  );

  const isSaving = createLoading || updateLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue
  } = useForm<OutTransactionFormData>({
    defaultValues: transaction ? transactionToOutFormValues(transaction) : {
      recipientType: 'employee',
      currency: 'EGP',
      moneyIn: false,
      amountToType: 'bank',
      transferType: 'transfer'
    }
  });

  const watchedRecipientType = watch('recipientType');

  const selectedCurrencyRaw = useWatch({ control, name: 'currency', defaultValue: 'EGP' });
  const selectedCurrencyCode = useMemo(
    () => resolveCurrencyCode(selectedCurrencyRaw, currencies as any[]),
    [selectedCurrencyRaw, currencies]
  );

  const isMoneyIn = useWatch({ control, name: 'moneyIn', defaultValue: false });
  const amountToType = useWatch({ control, name: 'amountToType', defaultValue: 'bank' });
  const selectedBankId = useWatch({ control, name: 'bankId', defaultValue: '' });
  const selectedSafeId = useWatch({ control, name: 'safeId', defaultValue: '' });
  const selectedTransferType = useWatch({ control, name: 'transferType', defaultValue: 'transfer' });
  const selectedBillId = useWatch({ control, name: 'billId', defaultValue: '' });
  const watchedAmount = useWatch({ control, name: 'amount', defaultValue: 0 });
  const watchedRate = useWatch({ control, name: 'exchangeRate' });

  const selectedBill = useMemo(
    () => (bills as any[])?.find((b: any) => (b.documentId || String(b.id)) === selectedBillId) || null,
    [bills, selectedBillId]
  );
  const billCurrencyCode = useMemo(
    () => (selectedBill ? resolveCurrencyCode(selectedBill.currency, currencies as any[]) : ''),
    [selectedBill, currencies]
  );

  React.useEffect(() => {
    setRecipientType(watchedRecipientType);
  }, [watchedRecipientType]);

  // Edit mode: native selects render before their option lists load, so the
  // saved value falls back to the placeholder. Re-apply each saved value once
  // its list is available (only once per field, so user changes aren't undone).
  const appliedRef = React.useRef<Record<string, boolean>>({});
  React.useEffect(() => {
    if (!isEdit) return;
    const saved = transactionToOutFormValues(transaction);
    const apply = (field: keyof OutTransactionFormData, list: any[] | null | undefined, raw?: string) => {
      if (appliedRef.current[field] || !raw || !list || list.length === 0) return;
      const match = list.find((x: any) => x.documentId === raw || String(x.id) === String(raw));
      setValue(field, (match ? (match.documentId || String(match.id)) : raw) as any);
      appliedRef.current[field] = true;
    };
    apply('supplierId', providers, saved.supplierId);
    apply('employeeId', employees, saved.employeeId);
    apply('billId', bills, saved.billId);
    apply('branchId', branches, saved.branchId);
    apply('bankId', banks, saved.bankId);
    apply('safeId', safes, saved.safeId);
    apply('currency', currencies, saved.currency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, transaction, providers, employees, bills, branches, banks, safes, currencies]);

  const getSelectedBankName = () => {
    if (!selectedBankId || !banks) return '';
    const bank = banks.find((b: any) => (b.documentId || b.id?.toString()) === selectedBankId);
    return bank ? bank.name : '';
  };

  const getSelectedSafeName = () => {
    if (!selectedSafeId || !safes) return '';
    const safe = safes.find((s: any) => (s.documentId || s.id?.toString()) === selectedSafeId);
    return safe ? safe.name : '';
  };

  const onSubmit = async (data: OutTransactionFormData) => {
    setSubmitError(null);
    // The rate is mandatory whenever a conversion is involved: non-EGP payment,
    // or an EGP payment applied against a foreign-currency bill
    const rateNeeded =
      selectedCurrencyCode !== 'EGP' ||
      (!!billCurrencyCode && billCurrencyCode !== 'EGP');
    if (rateNeeded && (!data.exchangeRate || Number(data.exchangeRate) <= 0)) {
      setSubmitError(
        selectedCurrencyCode !== 'EGP'
          ? `Exchange rate (${selectedCurrencyCode} to EGP) is required.`
          : `Exchange rate (${billCurrencyCode} to EGP) is required — the EGP amount will be divided by it to apply the payment to the ${billCurrencyCode} bill.`
      );
      return;
    }
    try {
      const transactionData: any = {
        type: 'out',
        description: data.description,
        amount: data.amount,
        currency: data.currency,
        date: data.transactionDate || new Date().toISOString().split('T')[0],
      };

      const resolvedCode = resolveCurrencyCode(data.currency, currencies as any[]);
      if (data.exchangeRate) {
        transactionData.exchangeRate = data.exchangeRate;
      }

      // Use *Id keys: the admin resource layer maps them to Strapi relation
      // names (bankId -> bank, categoryId -> category, branchId -> branch)
      if (data.amountToType === 'bank' && data.bankId) {
        transactionData.bankId = data.bankId;
      }

      if (data.amountToType === 'safe' && data.safeId) {
        transactionData.safeId = data.safeId;
      }

      if (!data.moneyIn && data.categoryId) {
        transactionData.categoryId = data.categoryId;
      }

      if (data.branchId) {
        transactionData.branchId = data.branchId;
      }

      if (!data.moneyIn) {
        transactionData.recipientType = data.recipientType;
        if (data.recipientType === 'employee' && data.employeeId) {
          transactionData.recipientId = data.employeeId;
        } else if (data.recipientType === 'supplier' && data.supplierId) {
          transactionData.recipientId = data.supplierId;
        }

        if (data.recipientType === 'supplier' && data.billId) {
          transactionData.bill = data.billId;
          // Amount converted into the bill's currency. EGP paid on a USD
          // bill: amount ÷ rate. USD paid on an EGP bill: amount × rate.
          let applied = Number(data.amount) || 0;
          if (billCurrencyCode && billCurrencyCode !== resolvedCode && data.exchangeRate) {
            if (resolvedCode === 'EGP' && billCurrencyCode !== 'EGP') {
              applied = applied / Number(data.exchangeRate);
            } else if (resolvedCode !== 'EGP' && billCurrencyCode === 'EGP') {
              applied = applied * Number(data.exchangeRate);
            }
          }
          transactionData.billAppliedAmount = Math.round(applied * 100) / 100;
        }
      }

      if (isEdit) {
        await updateTransaction({
          id: transaction.id,
          data: { ...transactionData, documentId: transaction.documentId },
        });
      } else {
        await createTransaction(transactionData);
      }
    } catch (error: any) {
      console.error('Error submitting transaction:', error);
      setSubmitError(error?.error?.message || 'Error submitting transaction. Please try again.');
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="px-8 py-6 bg-gradient-to-r from-ocean-600 to-ocean-700 text-white">
        <h2 className="text-2xl font-bold">{isEdit ? 'Edit Out Transaction' : 'Out Transaction'}</h2>
        <p className="text-ocean-100 mt-2">
          {isEdit ? 'Update this outgoing transaction' : 'Create a new outgoing transaction'}
        </p>
      </div>

      {submitSuccess && (
        <div className="mx-8 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium">Transaction created successfully!</p>
        </div>
      )}

      {submitError && (
        <div className="mx-8 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8">
        {isLoading && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">Loading form data...</p>
          </div>
        )}

        <OutBasicInfoSection
          register={register}
          errors={errors}
          currencyOptions={currencyOptions}
          selectedCurrencyCode={selectedCurrencyCode}
          billCurrencyCode={billCurrencyCode}
          amountValue={Number(watchedAmount) || 0}
          exchangeRateValue={Number(watchedRate) || 0}
        />

        <OutPaymentMethodSection
          register={register}
          errors={errors}
          bankOptions={bankOptions}
          safeOptions={safeOptions}
          branchOptions={branchOptions}
          amountToType={amountToType}
          isMoneyIn={!!isMoneyIn}
          banksLoading={banksLoading}
          safesLoading={safesLoading}
          branchesLoading={branchesLoading}
        />

        {amountToType === 'bank' && selectedBankId && (
          <OutBankDetailsSection
            register={register}
            errors={errors}
            selectedBankId={selectedBankId}
            selectedTransferType={selectedTransferType || 'transfer'}
            getSelectedBankName={getSelectedBankName}
          />
        )}

        {amountToType === 'safe' && selectedSafeId && (
          <OutSafeDetailsSection
            register={register}
            errors={errors}
            selectedSafeId={selectedSafeId}
            getSelectedSafeName={getSelectedSafeName}
          />
        )}

        <OutRecipientSection
          register={register}
          control={control}
          errors={errors}
          branchOptions={branchOptions}
          employeeOptions={employeeOptions}
          providerOptions={providerOptions}
          billOptions={billOptions}
          recipientType={recipientType}
          isMoneyIn={!!isMoneyIn}
          branchesLoading={branchesLoading}
          employeesLoading={employeesLoading}
          providersLoading={providersLoading}
          billsLoading={billsLoading}
        />

        {!!isMoneyIn && (
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Additional Notes
            </h3>
            <TextArea
              label="Notes"
              {...register('notes')}
              error={errors.notes?.message}
              placeholder="Enter any additional notes or comments about this transaction"
              rows={4}
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => { if (isEdit) { onCancel?.(); } else { reset(); } }}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {isEdit ? 'Cancel' : 'Reset Form'}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving ? 'Processing...' : isEdit ? 'Update Transaction' : 'Submit Transaction'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OutTransactionForm;
